import { ISearchContactsAdapterSearchResponse } from '@webex/component-adapter-interfaces/dist/esm/src';
import { useCallback, useContext, useRef, useState } from 'react';
import { getMSTeamsContext } from '../../../logger/src/WebexContext';
import { AdapterContext } from '../contexts/AdapterContext';
import { useDebouncedEffect } from './useDebouncedEffect';
// import { Logger } from '@webex-int/logger';

export enum StatusCode{
  SUCCESS = 200
 }

export interface UseContactSearchOptions {
  /** When true, directory results prefer Graph API then fallback to Search API (e.g. for Speed Dial). */
  preferGraphForDirectory?: boolean;
}

/**
 * Handles the searching of contacts, relaying the react state
 *
 * @param {string} searchText text to search for in contacts
 * @param {UseContactSearchOptions} options optional flags (e.g. preferGraphForDirectory for Speed Dial)
 * @returns {[string[], ISearchContactsAdapterSearchResponse, boolean]} array containing sources, search response, and loading boolean
 */
export const useContactSearch: (
  searchText: string,
  options?: UseContactSearchOptions
) => [string[], ISearchContactsAdapterSearchResponse, boolean] = (
  searchText: string,
  options?: UseContactSearchOptions
) => {
    const [items, setItems] = useState<ISearchContactsAdapterSearchResponse>({
      items: {},
      count: 0,
    });
    const [searching, setSearching] = useState<boolean>(false);
    const searchGenerationRef = useRef(0);
    const ctx = useContext(AdapterContext);
    const sources = ctx?.searchContactsAdapter?.getSources() || [];

    const getMSuserId = async () => {
      const context = await getMSTeamsContext();
      const userId = context.user?.id;
      return userId;
    }

    const searchContacts = useCallback(
      async (searchTextValue: string, generation: number) => {
        if (!ctx?.searchContactsAdapter) {
          console.error('context does not contain field searchContactsAdapter');
          return;
        }
        setSearching(true);
        try {
          if (!searchTextValue) {
            if (generation === searchGenerationRef.current) {
              setItems({ items: {}, count: 0 });
            }
            return;
          }

          // preferGraphForDirectory is supported by MsTeamsSearchContactsAdapter; cast needed for published @webex/component-adapter-interfaces type
          const data = await ctx.searchContactsAdapter.search({
            searchText: searchTextValue,
            ...(options?.preferGraphForDirectory !== undefined && { preferGraphForDirectory: options.preferGraphForDirectory }),
          } as any);

          if (generation !== searchGenerationRef.current) {
            return;
          }

          const msuserID = await getMSuserId();

          if (generation !== searchGenerationRef.current) {
            return;
          }

          const respItems = data?.items;
          let respCount = 0;
          sources.map(source => {
            respItems[source] = respItems[source]?.filter(res => res?.id !== msuserID);
            respCount = respCount + respItems[source].length;
          });
          data.items = respItems;
          data.count = respCount;

          setItems(data);
        } catch (e) {
          // Logger.error(
          //   `Contatc search error case: '${e}'.`
          // );
        } finally {
          if (generation === searchGenerationRef.current) {
            setSearching(false);
          }
        }
      },
      [ctx?.searchContactsAdapter, options?.preferGraphForDirectory]
    );

    useDebouncedEffect(
      () => searchContacts(searchText, searchGenerationRef.current),
      [searchText, searchContacts],
      StatusCode.SUCCESS,
      () => setSearching(true),
      () => {
        searchGenerationRef.current += 1;
      }
    );

    return [sources, items, searching];
  }; 